import { ASSETS } from "../lib/brand";
import { useReveal } from "../hooks/useReveal";

const MastheadRule = () => (
  <div className="flex items-center justify-center gap-5 md:gap-7 text-black/70">
    <span className="hidden sm:block h-px w-10 md:w-16 bg-black/60" />
    <span className="label-eyebrow">ISSUE I</span>
    <span className="opacity-40">·</span>
    <span className="label-eyebrow italic font-normal font-display text-[13px] tracking-normal">
      MMXXV
    </span>
    <span className="opacity-40">·</span>
    <span className="label-eyebrow">KW & BEYOND</span>
    <span className="hidden sm:block h-px w-10 md:w-16 bg-black/60" />
  </div>
);

export const Hero = () => {
  const logoRef = useReveal();
  const titleRef = useReveal();
  const gridRef = useReveal();

  const tiles = [
    { src: ASSETS.editorial1, alt: "Sunset, Mykonos",      tag: "N° 01 · Sunset" },
    { src: ASSETS.hero2,      alt: "Dinner, blue hour",    tag: "N° 02 · Dinner" },
    { src: ASSETS.hero3,      alt: "Nights, city lights",  tag: "N° 03 · Nights" },
    { src: ASSETS.hero4,      alt: "Afternoons, Cambridge",tag: "N° 04 · Afternoons" },
  ];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-24 md:pt-28 pb-14 md:pb-20 overflow-hidden"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 relative">
        {/* TOP: Logo centered */}
        <div
          ref={logoRef}
          className="reveal flex flex-col items-center text-center"
        >
          <img
            src={ASSETS.logo}
            alt="The Prismatics Co."
            data-testid="hero-logo"
            className="w-[200px] sm:w-[240px] md:w-[280px] h-auto object-contain select-none"
            loading="eager"
          />
        </div>

        {/* Masthead rule — magazine-style metadata */}
        <div
          data-testid="hero-masthead"
          className="-mt-1 md:-mt-2 flex items-center justify-center reveal reveal-delay-1"
        >
          <MastheadRule />
        </div>

        {/* Static slogan */}
        <div
          ref={titleRef}
          className="mt-6 md:mt-8 flex flex-col items-center text-center reveal reveal-delay-2"
        >
          <h1
            data-testid="hero-title"
            className="font-display tracking-[-0.015em] leading-[1.08] text-[7.5vw] sm:text-4xl md:text-5xl lg:text-[3.4rem] max-w-[30ch]"
          >
            <span className="font-normal">your world through our&nbsp;</span>
            <span className="italic">lenses</span>
            <span className="font-normal">.</span>
          </h1>

          <p
            data-testid="hero-subhead"
            className="font-body text-[12px] md:text-[13px] tracking-[0.12em] uppercase leading-[1.9] text-black/65 mt-5 flex flex-wrap items-center justify-center gap-x-3"
          >
            <span>Luxury camera rental</span>
            <span className="opacity-40">·</span>
            <span className="italic normal-case tracking-normal font-display text-[14px] md:text-[15px]">
              by inquiry only
            </span>
          </p>

          <div className="flex flex-wrap items-center justify-center gap-5 md:gap-6 mt-8">
            <a
              href="#contact"
              data-testid="hero-cta-book"
              className="btn-pill btn-pill-solid"
            >
              Book a rental
            </a>
            <a
              href="#occasions"
              data-testid="hero-cta-browse"
              className="label-micro link-underline"
            >
              Every occasion →
            </a>
          </div>
        </div>

        {/* 4-image editorial grid with asymmetric vertical offsets */}
        <div
          ref={gridRef}
          data-testid="hero-grid"
          className="mt-14 md:mt-20 reveal reveal-delay-3 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5"
        >
          {tiles.map((t, i) => (
            <figure
              key={i}
              data-testid={`hero-tile-${i}`}
              className={`group relative overflow-hidden aspect-[3/4] bg-black/5 ${
                i % 2 === 1 ? "md:mt-10 lg:mt-14" : "md:mt-0"
              }`}
            >
              <img
                src={t.src}
                alt={t.alt}
                loading={i < 2 ? "eager" : "lazy"}
                className="editorial-img absolute inset-0 w-full h-full object-cover"
              />
              <figcaption className="absolute bottom-3 left-3 md:bottom-4 md:left-4 label-eyebrow text-white mix-blend-difference opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {t.tag}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};
